/**
 * Side navigation drawer. On mobile: fixed overlay with a dark scrim. On desktop (≥1024px):
 * static 240px sidebar. Contains avatar + links (auth) or sign-in/up buttons (unauth),
 * dark-mode toggle, LanguageSelector, and sign-out.
 */
import { Home, Search, Bell, MessageSquare, User, Settings, Moon, Sun, Globe, LogOut, Info, FileText, Shield, LayoutDashboard, Download } from 'lucide-react'

const isPwaInstalled =
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as unknown as { standalone?: boolean }).standalone === true
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth.store'
import { Avatar } from '@/components/profile/Avatar'
import { useUiStore } from '@/store/ui.store'
import { useFeedStore } from '@/store/feed.store'
import { authApi, messagesApi, notificationsApi } from '@/api'
import { LanguageSelector } from './LanguageSelector'

interface NavDrawerProps {
  open: boolean
  onClose: () => void
}

function NavRow({
  icon,
  label,
  active,
  danger,
  disabled,
  badge,
  badgeCount,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  active?: boolean
  danger?: boolean
  disabled?: boolean
  badge?: string
  badgeCount?: number
  onClick?: () => void
}) {
  const color = danger ? 'var(--danger)' : disabled ? 'var(--text-faint)' : active ? 'var(--brand)' : 'var(--text-strong)'
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        textAlign: 'start',
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        minHeight: 'var(--tap-min)',
        padding: '10px 14px',
        borderRadius: 'var(--radius-md)',
        background: active ? 'var(--brand-soft)' : 'transparent',
        fontFamily: 'var(--font-sans)',
        fontWeight: active ? 700 : 500,
        fontSize: 'var(--fs-body)',
        color,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{ display: 'flex', flexShrink: 0, color: danger ? 'var(--danger)' : disabled ? 'var(--text-faint)' : active ? 'var(--brand)' : 'var(--text-muted)' }}>
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge && (
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 500 }}>
          {badge}
        </span>
      )}
      {badgeCount != null && badgeCount > 0 && (
        <span
          style={{
            minWidth: 18,
            height: 18,
            borderRadius: 'var(--radius-pill)',
            background: 'var(--danger)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
          }}
        >
          {badgeCount > 9 ? '9+' : badgeCount}
        </span>
      )}
    </button>
  )
}

function Divider() {
  return <div style={{ height: 1, background: 'var(--border)', margin: '6px 12px' }} />
}

export function NavDrawer({ open, onClose }: NavDrawerProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, account, clearAuth, hasCapability } = useAuthStore()
  const { theme, setTheme } = useUiStore()
  const { setFeed, selectedFeed } = useFeedStore()

  const path = location.pathname

  const { data: unreadDmCount = 0 } = useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: () => messagesApi.getUnreadCount(),
    enabled: isAuthenticated,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })

  const { data: unreadNotifCount = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.getUnreadCount(),
    enabled: isAuthenticated,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })

  async function handleSignOut() {
    try { await authApi.signout() } catch { /* ignore */ }
    clearAuth()
    onClose()
    navigate('/')
  }

  function go(to: string) {
    navigate(to)
    onClose()
  }

  return (
    <>
      {/* Scrim — mobile only */}
      <div
        className="lg:hidden"
        aria-hidden={!open}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 80,
          background: `rgba(8,10,15,${open ? 0.42 : 0})`,
          backdropFilter: open ? 'blur(2px)' : 'none',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity var(--dur-base) var(--ease-out)',
        }}
      />

      {/* Drawer — fixed on mobile, static sidebar on desktop */}
      <aside
        className={[
          'fixed lg:static',
          'inset-y-0 start-0',
          'z-[85] lg:z-auto',
          'w-[304px] max-w-[86%] lg:w-[240px] lg:max-w-none',
          'flex-shrink-0 flex flex-col',
          'shadow-[var(--shadow-modal)] lg:shadow-none',
          'lg:border-e lg:border-[var(--border)]',
          'transition-transform duration-300 ease-out lg:transition-none',
          open ? 'translate-x-0' : '-translate-x-[104%]',
          'lg:translate-x-0',
        ].join(' ')}
        style={{ background: 'var(--surface)' }}
      >
        <div className="oh-spectrum lg:hidden" style={{ flexShrink: 0, height: 5, borderRadius: 0 }} />

        {/* Identity header */}
        {isAuthenticated && account ? (
          <button
            onClick={() => go(`/@${account.username}`)}
            style={{
              flexShrink: 0,
              padding: '16px 16px 14px',
              borderBottom: '1px solid var(--border)',
              display: 'block',
              width: '100%',
              textAlign: 'start',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
            }}
          >
            <Avatar
              name={account.displayName || account.username}
              avatarUrl={account.avatarUrl}
              animalAvatar={account.animalAvatar}
              size="lg"
              style={{ width: 52, height: 52, fontSize: 20 }}
            />
            <div style={{ marginTop: 10, fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-body-lg)', color: 'var(--text-strong)' }}>
              {account.displayName}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>@{account.username}</div>
          </button>
        ) : (
          <div style={{ flexShrink: 0, padding: '16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10 }}>
            <button onClick={() => go('/login')} style={{ flex: 1, padding: '10px 0', border: '1.5px solid var(--border-strong)', borderRadius: 'var(--radius-pill)', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
              {t('auth.login_cta')}
            </button>
            <button onClick={() => go('/signup')} style={{ flex: 1, padding: '10px 0', border: 'none', borderRadius: 'var(--radius-pill)', background: 'var(--brand)', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--on-brand)' }}>
              {t('welcome.signup')}
            </button>
          </div>
        )}

        {/* Nav list */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isAuthenticated && (
            <>
              <NavRow icon={<Home size={22} />} label={t('nav.home')} active={path === '/' && selectedFeed === 'local'} onClick={() => { setFeed('local'); go('/') }} />
              <NavRow icon={<Search size={22} />} label={t('nav.search')} active={path === '/search'} onClick={() => go('/search')} />
              <NavRow icon={<Bell size={22} />} label={t('nav.notifications')} active={path.startsWith('/notifications')} badgeCount={unreadNotifCount} onClick={() => go('/notifications')} />
              <NavRow icon={<MessageSquare size={22} />} label={t('nav.messages')} active={path.startsWith('/messages')} badgeCount={unreadDmCount} onClick={() => go('/messages')} />
              <Divider />
              <NavRow icon={<User size={22} />} label={t('nav.profile')} active={path === `/@${account?.username}`} onClick={() => go(`/@${account?.username}`)} />
              <NavRow icon={<Settings size={22} />} label={t('nav.settings')} active={path.startsWith('/settings')} onClick={() => go('/settings')} />
              {hasCapability('moderate') && (
                <NavRow icon={<LayoutDashboard size={22} />} label={t('admin.dashboard')} active={path.startsWith('/admin')} onClick={() => go('/admin')} />
              )}
              <Divider />
            </>
          )}
          {!isPwaInstalled && <NavRow icon={<Download size={22} />} label={t('settings.how_to_install_title')} active={path.startsWith('/how-to-install')} onClick={() => go('/how-to-install')} />}
          <NavRow icon={<Info size={22} />} label={t('nav.about')} active={path.startsWith('/about')} onClick={() => go('/about')} />
          <NavRow icon={<FileText size={22} />} label={t('nav.conduct')} active={path.startsWith('/conduct')} onClick={() => go('/conduct')} />
          <NavRow icon={<Shield size={22} />} label={t('nav.privacy')} active={path.startsWith('/privacy')} onClick={() => go('/privacy')} />
        </nav>

        {/* Footer */}
        <div style={{ flexShrink: 0, borderTop: '1px solid var(--border)', padding: '10px 16px 16px' }}>
          {/* Dark mode toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
            <span style={{ display: 'flex', color: 'var(--text-muted)' }}>
              {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
            </span>
            <span style={{ flex: 1, fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 'var(--fs-body)', color: 'var(--text-strong)' }}>
              {t('settings.dark_mode')}
            </span>
            <button
              role="switch"
              aria-checked={theme === 'dark'}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              style={{
                width: 44, height: 26,
                borderRadius: 'var(--radius-pill)',
                border: 'none',
                background: theme === 'dark' ? 'var(--brand)' : 'var(--border-strong)',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background var(--dur-base) var(--ease-out)',
              }}
            >
              <span style={{
                position: 'absolute',
                top: 3,
                insetInlineStart: theme === 'dark' ? 'calc(100% - 23px)' : 3,
                width: 20, height: 20,
                borderRadius: '50%',
                background: '#fff',
                transition: 'inset-inline-start var(--dur-base) var(--ease-out)',
              }} />
            </button>
          </div>

          {/* Language — only shown for authenticated users; guests use the hero band selector */}
          {isAuthenticated && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
              <span style={{ display: 'flex', color: 'var(--text-muted)' }}><Globe size={20} /></span>
              <LanguageSelector dropUp />
            </div>
          )}

          {isAuthenticated && (
            <NavRow icon={<LogOut size={20} />} label={t('settings.sign_out')} danger onClick={handleSignOut} />
          )}

          <div style={{ marginTop: 12, fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
            olimhub.social · part of the fediverse 💙
          </div>
        </div>
      </aside>
    </>
  )
}
